import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { CpuIcon, BrainCircuitIcon, DollarSignIcon, BuildingIcon, TrendingUpIcon } from 'lucide-react';
const justinDomains = [{
  period: '2008 – 2014',
  title: 'Investments & Capital Markets',
  description: 'Martin & Company. Foundation in financial analysis, capital markets, and investment strategy.',
  icon: DollarSignIcon
}, {
  period: '2014 – 2015',
  title: 'Software Startups',
  description: 'K-Town Apartments & Boomerang Software. Early-stage ventures in property tech, project management, and asset tracking.',
  icon: CpuIcon
}, {
  period: '2016 – 2021',
  title: 'Healthcare Finance & Operations',
  description: 'Ballad Health. Hospital CFO and administrator roles. Outperformed EBITDA targets by 18%, identified $10M+ growth opportunities, and managed $100M in managed care revenue.',
  icon: BuildingIcon
}, {
  period: '2021 – 2025',
  title: 'Global Food Distribution & Trade',
  description: 'Apex Foods Global. Scaled from a two-person operation to a cross-functional organization. Secured $30M–$50M in new annual revenue and built a nationwide export distribution network.',
  icon: TrendingUpIcon
}, {
  period: '2026 – Present',
  title: 'Software & AVM',
  description: 'MeatMarket.AI + Atlas Ventures Management. Building AI-powered platforms for asset-heavy industries and managing the AVM portfolio.',
  icon: BrainCircuitIcon
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
interface DomainCard {
  period: string;
  title: string;
  description: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
}
function DomainGrid({
  domains
}: {
  domains: DomainCard[];
}) {
  return <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-80px'
  }} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-105">
      {domains.map(domain => {
      const Icon = domain.icon;
      return <motion.div key={domain.title} variants={itemVariants} className="group border border-avm-border bg-avm-bgAlt p-8 hover:border-avm-accent/50 transition-colors duration-500" data-id="element-106">
            <div className="flex items-center justify-between mb-8" data-id="element-107">
              <span className="text-xs font-medium tracking-wider text-avm-accent/80 uppercase" data-id="element-108">
                {domain.period}
              </span>
              <Icon className="w-5 h-5 text-avm-accent/50 group-hover:text-avm-accent transition-colors duration-500" strokeWidth={1.5} data-id="element-109" />
            </div>
            <h4 className="text-lg font-medium text-avm-text mb-3 tracking-tight" data-id="element-110">
              {domain.title}
            </h4>
            <p className="text-sm text-avm-textMuted font-light leading-relaxed" data-id="element-111">
              {domain.description}
            </p>
          </motion.div>;
    })}
    </motion.div>;
}
export function LeadershipSection() {
  return <section id="leadership" className="py-32 px-6 md:px-12 max-w-7xl mx-auto border-t border-avm-border" data-id="element-112">
      <div className="mb-20" data-id="element-113">
        <span className="text-avm-accent text-xs font-semibold tracking-[0.2em] uppercase mb-6 block" data-id="element-114">
          Leadership
        </span>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-avm-text mb-10" data-id="element-115">
          Operational & Technical Authority
        </h2>
        <p className="text-lg md:text-xl text-avm-text font-medium leading-relaxed max-w-4xl" data-id="element-116">
          AVM targets mature, asset-heavy sectors — protein distribution,
          construction, logistics — where outdated infrastructure creates
          predictable inefficiencies. The playbook: rigorous financial analysis
          to locate the gap, purpose-built AI and data systems to close it, and
          operational execution to compound the returns.
        </p>
      </div>

      {/* What AVM Brings */}
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1]
    }} className="mb-32" data-id="element-117">
        <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-avm-accent mb-8" data-id="element-118">
          What AVM Brings to Complex Projects
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-119">
          {[{
          title: 'Financial & Technical Diligence',
          description: 'CFA-trained cost structure analysis paired with engineering-level system audits — we quantify the opportunity and validate the technical path before committing resources.'
        }, {
          title: 'Operational Buildout',
          description: 'Scaled organizations from 2 to 8 people with SOPs, ERP, and logistics infra. Shipped production systems on millions of devices. We build and operate, not just advise.'
        }, {
          title: 'AI & Data System Design',
          description: 'From on-device sLLM inference to enterprise data pipelines — we frame AI investment in terms of EBITDA impact and payback period, then architect and deploy the solution.'
        }, {
          title: 'Cross-Industry Pattern Recognition',
          description: 'Healthcare, food distribution, logistics, IoT — the recurring theme: manual workflows, fragmented data, and underpriced risk. We see the pattern and know how to close the gap.'
        }].map(card => <div key={card.title} className="border border-avm-border bg-avm-bgAlt p-8 hover:border-avm-accent/50 transition-colors duration-500" data-id="element-120">
              <h5 className="text-base font-medium text-avm-text mb-4 tracking-tight" data-id="element-121">
                {card.title}
              </h5>
              <p className="text-sm text-avm-textMuted font-light leading-relaxed" data-id="element-122">
                {card.description}
              </p>
            </div>)}
        </div>
      </motion.div>

      {/* ── Justin Ivory ── */}
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-16" data-id="element-123">
        <div className="lg:col-span-5" data-id="element-124">
          <h3 className="font-serif text-3xl md:text-4xl text-avm-text mb-2" data-id="element-125">
            Justin Ivory
          </h3>
          <p className="text-avm-accent font-medium text-sm tracking-widest uppercase mb-8" data-id="element-126">
            Founder & Managing Partner
          </p>
          <div className="space-y-3 text-sm text-avm-textMuted font-light" data-id="element-127">
            <p data-id="element-128">CFA® Charterholder</p>
            <p data-id="element-129">MBA — USC Marshall School of Business</p>
            <p data-id="element-130">Published commentator, The National Provisioner</p>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col justify-end" data-id="element-131">
          <p className="text-lg md:text-xl text-avm-textMuted font-light leading-relaxed" data-id="element-132">
            A career built on transforming underperforming operations into
            scalable, financially disciplined businesses. As CFO across Ballad
            Health's hospital system, outperformed EBITDA targets by 18%, led a
            $2B Epic ERP transition driving $10M in net revenue recovery, and
            identified $30M+ in pricing opportunities. At Apex Foods Global,
            scaled a two-person operation into a cross-functional organization
            generating $30M–$50M in new annual revenue, while delivering $1M+ in
            recurring efficiencies through ERP modernization and logistics
            redesign. Brings CFA-level financial rigor to every operational
            decision.
          </p>
        </div>
      </motion.div>

      <div data-id="element-133">
        <DomainGrid domains={justinDomains} data-id="element-134" />
      </div>
    </section>;
}